/**
 * Step 1 — Segmentation Review & Perfused Volume Builder.
 *
 * Displays Couinaud segments and tumors with volumes,
 * allows creating perfused volumes from segment/tumor selections.
 */

import { Plus } from 'lucide-react';
import { useState, useCallback } from 'react';

import { PerfusedVolumeBuilder } from './perfused-volume-builder';
import { PerfusedVolumeCard } from './perfused-volume-card';
import { SegmentList } from './segment-list';
import { useDosimetryState } from '../hooks/use-dosimetry-state';
import { DosimetryStateService } from '../services/dosimetry-state-service';

import { Button } from '@/components/ui/button';

// ============================================================================
// Component
// ============================================================================

export function DosimetrySegmentationPanel() {
    const state = useDosimetryState();
    const [building, setBuilding] = useState(false);
    const [editingPvId, setEditingPvId] = useState<string | null>(null);

    const segments = state?.segmentEntries ?? [];
    const tumors = state?.tumorEntries ?? [];
    const totalLiverVolumeMl = state?.totalLiverVolumeMl ?? 0;

    // ── Highlight callbacks ──

    const onHoverSegment = useCallback(
        (labelValue: number | null) => {
            state?.setHovered(
                labelValue !== null ? DosimetryStateService.structureKey('segment', labelValue) : null
            );
        },
        [state]
    );

    const onHoverTumor = useCallback(
        (labelValue: number | null) => {
            state?.setHovered(
                labelValue !== null ? DosimetryStateService.structureKey('tumor', labelValue) : null
            );
        },
        [state]
    );

    const onSelectionChanged = useCallback(
        (selectedSegments: ReadonlySet<number>, selectedTumors: ReadonlySet<number>) => {
            const keys = new Set<string>();
            for (const lv of selectedSegments) keys.add(DosimetryStateService.structureKey('segment', lv));
            for (const lv of selectedTumors) keys.add(DosimetryStateService.structureKey('tumor', lv));
            state?.setSelectedStructures(keys);
        },
        [state]
    );

    // ── Handlers ──

    if (!state) {
        return (
            <div className="flex flex-col gap-4 p-4">
                <p className="text-xs text-muted-foreground">Loading segmentation data...</p>
            </div>
        );
    }

    const editingPv = editingPvId ? state.perfusedVolumes.find((pv) => pv.id === editingPvId) : null;

    const handleSave = (segmentIndices: number[], tumorIndices: number[], name?: string) => {
        if (editingPvId) {
            state.replacePerfusedVolume(editingPvId, {
                segmentIndices,
                tumorIndices,
                name,
            });
            setEditingPvId(null);
        } else {
            state.addPerfusedVolume(segmentIndices, tumorIndices, name);
        }
        state.clearHighlight();
        setBuilding(false);
    };

    const handleCancel = () => {
        state.clearHighlight();
        setBuilding(false);
        setEditingPvId(null);
    };

    const handleRemove = (id: string) => {
        state.removePerfusedVolume(id);
    };

    const handleEdit = (id: string) => {
        setEditingPvId(id);
        setBuilding(true);
    };

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
                {building || editingPvId ? (
                    <PerfusedVolumeBuilder
                        segments={segments}
                        tumors={tumors}
                        totalLiverVolumeMl={totalLiverVolumeMl}
                        computeUnionVolumeMl={state.computeUnionVolumeMl}
                        initialSegments={editingPv ? [...editingPv.segmentIndices] : []}
                        initialTumors={editingPv ? [...editingPv.tumorIndices] : []}
                        initialName={editingPv?.name}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        onHoverSegment={onHoverSegment}
                        onHoverTumor={onHoverTumor}
                        onSelectionChanged={onSelectionChanged}
                    />
                ) : (
                    <>
                        {/* Segment list (read-only mode) */}
                        <SegmentList
                            segments={segments}
                            tumors={tumors}
                            totalLiverVolumeMl={totalLiverVolumeMl}
                            onHoverSegment={onHoverSegment}
                            onHoverTumor={onHoverTumor}
                        />

                        {/* Perfused volumes */}
                        <div className="border-b border-border/50 last:border-b-0">
                            <div className="flex w-full items-center justify-between gap-1 px-2 py-2">
                                <span className="text-sm font-semibold font-mono uppercase tracking-wide text-muted-foreground">
                                    [Perfused Volumes]
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => setBuilding(true)}
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    New
                                </Button>
                            </div>
                            <div className="flex flex-col gap-1.5 px-2 pb-2">
                                {state.perfusedVolumes.length === 0 ? (
                                    <p className="text-[11px] text-muted-foreground px-1">
                                        No perfused volumes defined. Click "New" to create one.
                                    </p>
                                ) : (
                                    state.perfusedVolumes.map((pv) => (
                                        <PerfusedVolumeCard
                                            key={pv.id}
                                            pv={pv}
                                            isSelected={state.selectedPvId === pv.id}
                                            onSelect={state.selectPerfusedVolume}
                                            onEdit={handleEdit}
                                            onRemove={handleRemove}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
